import { toast } from '@/shared/lib/toast';
import { wsLogger } from '@/shared/lib/logger';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { payloadNumber, payloadString } from '../realtime-sync.payload';
import type { DataChangedEvent } from '../realtime-sync.types';
import type { ProjectReferencesListResult } from '@/shared/api/services/project-references.service';

export async function handleReferenceUpdated(event: DataChangedEvent): Promise<void> {
  const payload = event.payload;
  const projectId = payloadString(payload, 'project_id');

  wsLogger.debug('[RealtimeSync] Reference updated:', {
    referenceId: event.id,
    projectId,
  });

  if (!projectId) return;

  // Optimistic re-typing of the Brief tile. Classification takes 15-25s, so the
  // invalidation below only repaints the caption once the refetch lands — long
  // enough that the tile reads as "the drawing wasn't understood". The event
  // carries the server-composed badge, so patch it straight into the cached row
  // and the tile flips the instant the sheet is typed. The invalidate still
  // runs right after: the API stays the source of truth (realtime reconcile).
  const documentBadge = payloadString(payload, 'document_badge');
  if (documentBadge) {
    const documentRole = payloadString(payload, 'document_role') ?? null;
    const documentLevel = payloadNumber(payload, 'document_level') ?? null;
    const documentSide = payloadString(payload, 'document_side') ?? null;

    queryClient.setQueriesData<ProjectReferencesListResult>(
      { queryKey: cacheKeys.references.listsByProject(projectId) },
      (current) => {
        if (!current?.references?.some((ref) => ref.id === event.id)) {
          return current;
        }
        return {
          ...current,
          references: current.references.map((ref) =>
            ref.id === event.id
              ? {
                  ...ref,
                  attributes: {
                    ...ref.attributes,
                    document_role: documentRole,
                    document_level: documentLevel,
                    document_side: documentSide,
                    document_badge: documentBadge,
                  },
                }
              : ref
          ),
        };
      }
    );
  }

  queryClient.invalidateQueries({
    queryKey: cacheKeys.references.listsByProject(projectId),
  });
}

export async function handleReferenceRejected(event: DataChangedEvent): Promise<void> {
  const payload = event.payload;

  wsLogger.warn('[RealtimeSync] Reference rejected:', {
    referenceId: event.id,
    projectId: payload?.project_id,
    reason: payload?.reason,
  });

  // Show toast notification — API-driven only.
  const referenceRejectedTitle = payloadString(payload, 'title');
  if (referenceRejectedTitle) {
    const description = payloadString(payload, 'message');
    toast.error(referenceRejectedTitle, {
      ...(description && { description }),
      duration: 8000,
    });
  }

  // Invalidate project references query
  const projectId = payloadString(payload, 'project_id');
  if (projectId) {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.references.listsByProject(projectId),
    });
  }
}
